import axios from 'axios';
import { setupAuthInterceptor } from './authInterceptor';

const getApiBaseUrl = (): string => {
  // Get API URL from window.ENV (loaded from config.js)
  const apiUrl = window.ENV?.VITE_API_BASE_URL;

  if (!apiUrl) {
    console.error('⚠️ window.ENV.VITE_API_BASE_URL is not set! Check that config.js is loaded properly.');
    console.error('Current window.ENV:', window.ENV);
    // Use an obviously broken URL so it's immediately clear the config failed to load
    return 'http://config.not.loaded.xxx';
  }

  return apiUrl;
};

const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Setup automatic token refresh interceptor
setupAuthInterceptor(apiClient);

/**
 * Get authentication headers for API requests
 */
const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('accessToken');

  if (token) {
    return { 'Authorization': `Bearer ${token}` };
  }

  return {};
};

// Types for billing API responses
export interface BalanceResponse {
  balance_usd: number;
  free_credits_usd: number;
  cost_markup_multiplier: number;
  total_spent_usd: number;
  total_purchased_usd: number;
  is_low_balance: boolean;
  is_zero_balance: boolean;
}

export interface CreditPackage {
  id: number;
  stripe_product_id: string;
  stripe_price_id: string;
  name: string;
  description: string;
  credit_amount_usd: number;
  price_cents: number;
  currency: string;
  display_price: string;
}

export interface CreditPackagesResponse {
  packages: CreditPackage[];
}

export interface CreateCheckoutSessionRequest {
  price_id: string;
  success_url: string;
  cancel_url: string;
}

export interface CreateCheckoutSessionResponse {
  checkout_url: string;
  session_id: string;
}

export interface BalanceTransaction {
  id: number;
  transaction_type: string;
  amount_usd: number;
  balance_before_usd: number;
  balance_after_usd: number;
  description: string;
  created_at: string;
}

export interface TransactionsResponse {
  transactions: BalanceTransaction[];
  total_count: number;
  limit: number;
  offset: number;
}

export interface RedeemCouponRequest {
  code: string;
}

export interface RedeemCouponResponse {
  success: boolean;
  credit_amount: number;
  new_balance: number;
  message: string;
}

export interface Coupon {
  id: number;
  stripe_coupon_id: string;
  code: string;
  credit_amount_usd: number;
  description: string | null;
  max_redemptions: number | null;
  redemption_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  deactivated_at: string | null;
}

export interface CouponRedemption {
  id: number;
  user_id: string;
  user_name: string;
  user_email: string | null;
  credit_amount_usd: number;
  redeemed_at: string;
}

export interface CouponWithRedemptions extends Coupon {
  redemptions: CouponRedemption[];
}

export interface CreateCouponRequest {
  code: string;
  credit_amount: number;
  description?: string;
  max_redemptions?: number;
}

export const billingService = {
  /**
   * Get current user balance and spending statistics
   */
  getBalance: async (): Promise<BalanceResponse> => {
    const headers = getAuthHeaders();

    const response = await apiClient.get<BalanceResponse>(
      '/api/billing/balance',
      { headers }
    );

    return response.data;
  },

  /**
   * Get available credit packages for purchase
   */
  getCreditPackages: async (): Promise<CreditPackage[]> => {
    const headers = getAuthHeaders();

    const response = await apiClient.get<CreditPackagesResponse>(
      '/api/billing/credit-packages',
      { headers }
    );

    return response.data.packages;
  },

  /**
   * Create Stripe Checkout session for credit purchase
   */
  createCheckoutSession: async (
    request: CreateCheckoutSessionRequest
  ): Promise<CreateCheckoutSessionResponse> => {
    const headers = getAuthHeaders();

    const response = await apiClient.post<CreateCheckoutSessionResponse>(
      '/api/billing/create-checkout-session',
      request,
      { headers }
    );

    return response.data;
  },

  /**
   * Get user transaction history
   */
  getTransactions: async (limit: number = 50, offset: number = 0): Promise<TransactionsResponse> => {
    const headers = getAuthHeaders();

    const response = await apiClient.get<TransactionsResponse>(
      `/api/billing/transactions?limit=${limit}&offset=${offset}`,
      { headers }
    );

    return response.data;
  },

  /**
   * Redeem a coupon code for free credits
   */
  redeemCoupon: async (request: RedeemCouponRequest): Promise<RedeemCouponResponse> => {
    const headers = getAuthHeaders();

    const response = await apiClient.post<RedeemCouponResponse>(
      '/api/billing/redeem-coupon',
      request,
      { headers }
    );

    return response.data;
  },

  /**
   * Admin: Get all coupons
   */
  getAllCoupons: async (): Promise<Coupon[]> => {
    const headers = getAuthHeaders();

    const response = await apiClient.get<{ coupons: Coupon[] }>(
      '/api/billing/admin-coupons/list',
      { headers }
    );

    return response.data.coupons;
  },

  /**
   * Admin: Create a new coupon
   */
  createCoupon: async (request: CreateCouponRequest): Promise<Coupon> => {
    const headers = getAuthHeaders();

    const response = await apiClient.post<{ coupon: Coupon }>(
      '/api/billing/admin-coupons/create',
      request,
      { headers }
    );

    return response.data.coupon;
  },

  /**
   * Admin: Get coupon with redemption history
   */
  getCouponWithRedemptions: async (couponId: number): Promise<CouponWithRedemptions> => {
    const headers = getAuthHeaders();

    const response = await apiClient.get<{ coupon: CouponWithRedemptions }>(
      `/api/billing/admin-coupons/${couponId}/redemptions`,
      { headers }
    );

    return response.data.coupon;
  },

  /**
   * Admin: Deactivate a coupon
   */
  deactivateCoupon: async (couponId: number): Promise<void> => {
    const headers = getAuthHeaders();

    await apiClient.patch(
      `/api/billing/admin-coupons/${couponId}/deactivate`,
      {},
      { headers }
    );
  },

  /**
   * Initiate TFA verification for coupon redemption
   */
  initiateTfa: async (request: { couponCode: string }): Promise<{ success: true; message: string; expiresInMinutes: number }> => {
    const headers = getAuthHeaders();

    const response = await apiClient.post(
      '/api/billing/redeem-coupon/initiate-tfa',
      request,
      { headers }
    );

    return response.data;
  },

  /**
   * Verify TFA code and redeem coupon
   */
  verifyTfa: async (request: { couponCode: string; verificationCode: string }): Promise<RedeemCouponResponse> => {
    const headers = getAuthHeaders();

    const response = await apiClient.post<RedeemCouponResponse>(
      '/api/billing/redeem-coupon/verify-tfa',
      request,
      { headers }
    );

    return response.data;
  },
};
